import { useEffect, useRef, useState } from 'react';
import {
  Box, Button, FormControl, FormLabel, HStack, Modal, ModalBody, ModalCloseButton,
  ModalContent, ModalFooter, ModalHeader, ModalOverlay, Progress, Stack, Switch,
  Text, useToast,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { startVideoRender, VIDEO_FORMATS, VideoFormat, VIDEO_STYLES, VideoStyleChoice, RenderHandle } from './videoRenderer';

/**
 * "Make video" studio. Runs entirely client-side — no server compute:
 * canvas draws each frame in sync with the audio via Web Audio API,
 * MediaRecorder captures the composite (canvas video + audio graph)
 * into a webm blob the user can download.
 *
 * Trade-offs baked into the UX:
 *   • Real-time recording — a 3-minute song takes 3 minutes to render.
 *     Faster-than-realtime would need our own webm muxer, which is a
 *     bigger project than the first pass. The progress bar makes the
 *     wait visible.
 *   • WebM output — every social platform (YouTube, TikTok, IG, Reels)
 *     accepts webm as of 2026, so we skip the MP4 transcode step.
 *   • Muted while rendering — playing audio through the AudioContext
 *     graph would double-play (element + graph); we keep the element
 *     muted and route sound through the graph.
 */
export interface VideoStudioTrack {
  id: string;
  title: string;
  artist?: string;
  audioUrl: string;
  coverUrl?: string;
  /** Optional lyrics — enables the karaoke overlay. */
  lyrics?: string;
  /**
   * Word-timed lyrics from the provider (Mureka today). When present the
   * karaoke overlay uses real ms-accurate timing per line/word and IGNORES
   * `lyrics`. Absent → the renderer estimates line timing from `lyrics`.
   */
  syncedLyrics?: {
    lines: Array<{
      startMs: number;
      endMs: number;
      text: string;
      words?: Array<{ startMs: number; endMs: number; text: string }>;
    }>;
  };
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  track: VideoStudioTrack | null;
}

export function VideoStudioModal({ isOpen, onClose, track }: Props) {
  const { t } = useTranslation();
  const toast = useToast();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const handleRef = useRef<RenderHandle | null>(null);
  const [format, setFormat] = useState<VideoFormat>(VIDEO_FORMATS[0]);
  const [watermark, setWatermark] = useState(true);
  const [showLyrics, setShowLyrics] = useState(true);
  // 'random' is the intentional default so every render surprises the
  // user — turns the studio into a slot machine for looks. Power users
  // who want a locked style can tap a specific chip.
  const [style, setStyle] = useState<VideoStyleChoice>('random');
  const [status, setStatus] = useState<'idle' | 'rendering' | 'ready' | 'error'>('idle');
  const [progress, setProgress] = useState(0);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);

  // Reset on close so the next open is fresh.
  useEffect(() => {
    if (!isOpen) {
      handleRef.current?.cancel();
      handleRef.current = null;
      if (downloadUrl) URL.revokeObjectURL(downloadUrl);
      setDownloadUrl(null);
      setStatus('idle');
      setProgress(0);
      setErrorMsg(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const startRender = async () => {
    if (!track || !canvasRef.current) return;
    setStatus('rendering');
    setProgress(0);
    setErrorMsg(null);
    setDownloadUrl(null);
    try {
      const handle = await startVideoRender({
        audioUrl: track.audioUrl,
        coverUrl: track.coverUrl,
        title: track.title,
        artist: track.artist || 'MixGenie',
        lyrics: track.lyrics,
        syncedLyrics: track.syncedLyrics,
        showLyrics: showLyrics && (!!track.syncedLyrics || !!track.lyrics),
        style,
        format,
        watermark,
        canvas: canvasRef.current,
        onProgress: (fraction) => setProgress(Math.round(fraction * 100)),
        // Surface cover-load failures as a non-blocking warning. The render
        // continues with the title-card placeholder — the user just knows why.
        onCoverFailed: (msg) => toast({
          title: t('video.coverFailed', 'Cover art didn\'t load — using a title card'),
          description: msg,
          status: 'warning',
          duration: 4000,
        }),
      });
      handleRef.current = handle;
      const blob = await handle.done;
      const url = URL.createObjectURL(blob);
      setDownloadUrl(url);
      setStatus('ready');
      setProgress(100);
    } catch (err: any) {
      const msg = String(err?.message || err) || 'Video render failed';
      setErrorMsg(msg);
      setStatus('error');
      toast({ title: 'Video render failed', description: msg, status: 'error', duration: 6000 });
    }
  };

  const cancel = () => {
    handleRef.current?.cancel();
    handleRef.current = null;
    if (status === 'rendering') setStatus('idle');
  };

  const download = () => {
    if (!downloadUrl || !track) return;
    const ext = downloadUrl.includes('mp4') ? 'mp4' : 'webm';
    const filename = `${sanitizeFilename(track.title || 'song')}-${format.key.replace(':', 'x')}.${ext}`;
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = filename;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={{ base: 'full', md: 'lg' }} isCentered scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent bg="night.800" color="whiteAlpha.900">
        <ModalHeader>
          <Text fontSize="xs" color="whiteAlpha.600" textTransform="uppercase" letterSpacing="0.08em">
            {t('video.eyebrow', 'Make video')}
          </Text>
          <Text fontSize="md" noOfLines={2}>{track?.title || ''}</Text>
        </ModalHeader>
        <ModalCloseButton isDisabled={status === 'rendering'} />
        <ModalBody>
          <Stack spacing={5}>
            {/* Format picker */}
            <Stack spacing={2}>
              <Text fontSize="xs" color="whiteAlpha.600" textTransform="uppercase" letterSpacing="0.06em">
                {t('video.formatLabel', 'Format')}
              </Text>
              <HStack spacing={2} wrap="wrap">
                {VIDEO_FORMATS.map((f) => {
                  const active = format.key === f.key;
                  return (
                    <Button
                      key={f.key}
                      size="sm"
                      variant={active ? 'solid' : 'outline'}
                      colorScheme={active ? 'pink' : 'whiteAlpha'}
                      color={active ? undefined : 'whiteAlpha.900'}
                      borderColor="whiteAlpha.300"
                      isDisabled={status === 'rendering'}
                      onClick={() => setFormat(f)}
                    >
                      <Stack spacing={0} align="flex-start">
                        <Text fontSize="xs" fontWeight={700}>{f.key}</Text>
                        <Text fontSize="10px" opacity={0.8}>{f.label}</Text>
                      </Stack>
                    </Button>
                  );
                })}
              </HStack>
            </Stack>

            {/* Background style picker — one horizontal-scroll row instead
                of wrapping to 3 lines. Saves ~120px of vertical space in
                the modal on mobile; snap-x gives thumb-friendly scrolling
                on TikTok-style vertical layouts. */}
            <Stack spacing={2}>
              <Text fontSize="xs" color="whiteAlpha.600" textTransform="uppercase" letterSpacing="0.06em">
                {t('video.styleLabel', 'Background')}
              </Text>
              <HStack
                spacing={2}
                overflowX="auto"
                pb={1}
                sx={{
                  scrollSnapType: 'x proximity',
                  scrollbarWidth: 'none',
                  '&::-webkit-scrollbar': { display: 'none' },
                }}
              >
                {(['random', ...VIDEO_STYLES.map((s) => s.key)] as VideoStyleChoice[]).map((k) => {
                  const active = style === k;
                  const label = k === 'random'
                    ? `🎲 ${t('video.styleRandom', 'Random')}`
                    : VIDEO_STYLES.find((s) => s.key === k)?.label || k;
                  return (
                    <Button
                      key={k}
                      size="sm"
                      variant={active ? 'solid' : 'outline'}
                      colorScheme={active ? 'pink' : 'whiteAlpha'}
                      color={active ? undefined : 'whiteAlpha.900'}
                      borderColor="whiteAlpha.300"
                      isDisabled={status === 'rendering'}
                      onClick={() => setStyle(k)}
                      flexShrink={0}
                      sx={{ scrollSnapAlign: 'start' }}
                    >
                      <Text fontSize="xs" fontWeight={700}>{label}</Text>
                    </Button>
                  );
                })}
              </HStack>
              {style === 'random' && (
                <Text fontSize="xs" color="whiteAlpha.500">
                  {t('video.styleRandomHint', 'A different look each time you render — great for shipping variants without deciding.')}
                </Text>
              )}
            </Stack>

            {/* Watermark toggle */}
            <FormControl display="flex" alignItems="center" gap={3}>
              <Switch
                id="video-watermark-switch"
                isChecked={watermark}
                onChange={(e) => setWatermark(e.target.checked)}
                isDisabled={status === 'rendering'}
                colorScheme="pink"
              />
              <FormLabel htmlFor="video-watermark-switch" mb={0} fontSize="sm" fontWeight={500} cursor="pointer">
                {t('video.watermark', 'Show mixgenie.ai watermark')}
              </FormLabel>
            </FormControl>

            {/* Karaoke lyrics toggle. Only enabled when we actually have
                lyrics for this track — otherwise there's nothing to sync. */}
            <FormControl display="flex" alignItems="flex-start" gap={3}>
              <Switch
                id="video-karaoke-switch"
                mt="2px"
                isChecked={showLyrics && (!!track?.syncedLyrics || !!track?.lyrics)}
                onChange={(e) => setShowLyrics(e.target.checked)}
                isDisabled={status === 'rendering' || (!track?.syncedLyrics && !track?.lyrics)}
                colorScheme="pink"
              />
              <FormLabel htmlFor="video-karaoke-switch" mb={0} cursor="pointer" fontWeight={500}>
                <Stack spacing={0} align="flex-start">
                  <Text fontSize="sm">
                    {track?.syncedLyrics
                      ? t('video.karaokeSynced', 'Karaoke lyrics (word-accurate)')
                      : t('video.karaoke', 'Karaoke lyrics (estimated sync)')}
                  </Text>
                  {!track?.syncedLyrics && !track?.lyrics && (
                    <Text fontSize="xs" color="whiteAlpha.500">
                      {t('video.karaokeNoLyrics', 'This song has no lyrics on file.')}
                    </Text>
                  )}
                  {track?.syncedLyrics && (
                    <Text fontSize="xs" color="green.300">
                      {t('video.karaokeSyncedHint', 'Word timing from the provider — hits every syllable.')}
                    </Text>
                  )}
                  {!track?.syncedLyrics && track?.lyrics && (
                    <Text fontSize="xs" color="whiteAlpha.600">
                      {t('video.karaokeHint', 'Line timing is estimated from the song length — expect to be within a beat or two.')}
                    </Text>
                  )}
                </Stack>
              </FormLabel>
            </FormControl>


            {/* Preview canvas — recorder writes real 720p/1080p pixels
                regardless of display size (intrinsic dimensions come
                from the selected format). Container has a hard height
                (`dvh` — respects iOS/Android collapsing URL bar, unlike
                `vh`); canvas fills it while keeping the format's aspect.
                A canvas with `width:auto height:auto aspectRatio` alone
                fell back to its 300×150 default and blew past the cap. */}
            <Box
              bg="black"
              borderRadius="12px"
              overflow="hidden"
              border="1px solid"
              borderColor="whiteAlpha.150"
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="34dvh"
              maxH="34dvh"
            >
              <canvas
                ref={canvasRef}
                style={{
                  maxHeight: '100%',
                  maxWidth: '100%',
                  aspectRatio: `${format.width} / ${format.height}`,
                  display: 'block',
                  background: '#050310',
                }}
              />
            </Box>

            {status === 'rendering' && (
              <Stack spacing={2}>
                <Progress value={progress} size="sm" colorScheme="pink" borderRadius="full" bg="whiteAlpha.100" />
                <Text fontSize="xs" color="whiteAlpha.600" textAlign="center">
                  {t('video.rendering', 'Rendering in real time — the song will play through once.')} ({progress}%)
                </Text>
              </Stack>
            )}

            {status === 'error' && errorMsg && (
              <Text fontSize="sm" color="red.300">{errorMsg}</Text>
            )}

            {status === 'ready' && (
              <Text fontSize="sm" color="green.300">
                {t('video.ready', 'Video is ready — tap Download to save.')}
              </Text>
            )}
          </Stack>
        </ModalBody>
        <ModalFooter gap={2} flexWrap="wrap">
          {status === 'rendering' ? (
            <Button variant="outline" colorScheme="whiteAlpha" color="whiteAlpha.900" onClick={cancel}>
              {t('video.cancel', 'Stop')}
            </Button>
          ) : (
            <>
              <Button variant="ghost" color="whiteAlpha.800" onClick={onClose}>
                {t('close', 'Close')}
              </Button>
              <Button
                colorScheme="pink"
                onClick={startRender}
                isDisabled={!track}
                data-cta="video:render"
              >
                {status === 'ready'
                  ? t('video.renderAgain', 'Render again')
                  : t('video.render', 'Render video')}
              </Button>
              {status === 'ready' && downloadUrl && (
                <Button colorScheme="pink" variant="solid" onClick={download} data-cta="video:download">
                  ⬇ {t('video.download', 'Download')}
                </Button>
              )}
            </>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

function sanitizeFilename(name: string): string {
  return (name || 'song').replace(/[^\w\-. ()]+/g, '_').slice(0, 80).trim() || 'song';
}
